import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Users, TrendingUp, Heart, Crown, Shield, CheckCircle2, XCircle } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: '¿Por Qué',
    titleHighlight: 'MU Vanguard?',
    subtitle: 'No somos solo otro servidor privado. Somos excelencia.',
    vanguard: 'MU Vanguard',
    otherServers: 'Otros Servidores',
    features: [{
      icon: Zap,
      title: 'Desarrollo Activo',
      good: 'Actualizaciones semanales, bugs corregidos en 24-48h.',
      bad: 'Servidor abandonado después de 3 meses. Bugs sin corregir por semanas.'
    }, {
      icon: TrendingUp,
      title: 'Economía Balanceada',
      good: 'Tasas ajustadas. Anti-duping activo. Mercado estable.',
      bad: 'Inflación descontrolada. Dupers arruinan el mercado. Economía muerta.'
    }, {
      icon: Crown,
      title: 'Infraestructura Premium',
      good: 'Servidores dedicados. 99.9% uptime. Backups cada hora. DDoS protection.',
      bad: 'Hosting barato compartido. Caídas frecuentes. Sin backups. Vulnerable.'
    }, {
      icon: Heart,
      title: 'Comunidad Primero',
      good: 'Votaciones comunitarias. Discord activo. Eventos por jugadores.',
      bad: 'Comunidad tóxica sin moderación. Feedback ignorado. Sin eventos.'
    }, {
      icon: Users,
      title: 'Staff Profesional',
      good: 'Equipo 24/7. Soporte rápido. Decisiones transparentes.',
      bad: 'Staff ausente. Tickets ignorados. Decisiones arbitrarias.'
    }],
    commitment: 'Nuestro Compromiso',
    commitmentText: 'MU Vanguard no es un proyecto temporal. Es una inversión a largo plazo en crear la mejor experiencia de MU Online posible. Cada decisión se toma pensando en la salud del servidor y la satisfacción de la comunidad.'
  },
  en: {
    title: 'Why',
    titleHighlight: 'MU Vanguard?',
    subtitle: "We're not just another private server. We're excellence.",
    vanguard: 'MU Vanguard',
    otherServers: 'Other Servers',
    features: [{
      icon: Zap,
      title: 'Active Development',
      good: 'Weekly updates, bugs fixed within 24-48h.',
      bad: 'Server abandoned after 3 months. Bugs unfixed for weeks.'
    }, {
      icon: TrendingUp,
      title: 'Balanced Economy',
      good: 'Tuned rates. Active anti-duping. Stable market.',
      bad: 'Uncontrolled inflation. Dupers ruin the market. Dead economy.'
    }, {
      icon: Crown,
      title: 'Premium Infrastructure',
      good: 'Dedicated servers. 99.9% uptime. Hourly backups. DDoS protection.',
      bad: 'Cheap shared hosting. Frequent crashes. No backups. Vulnerable.'
    }, {
      icon: Heart,
      title: 'Community First',
      good: 'Community votes. Active Discord. Player-organized events.',
      bad: 'Toxic community without moderation. Feedback ignored. No events.'
    }, {
      icon: Users,
      title: 'Professional Staff',
      good: '24/7 team. Fast support. Transparent decisions.',
      bad: 'Absent staff. Ignored tickets. Arbitrary decisions.'
    }],
    commitment: 'Our Commitment',
    commitmentText: "MU Vanguard isn't a temporary project. It's a long-term investment in creating the best MU Online experience possible. Every decision is made with server health and community satisfaction in mind."
  },
  pt: {
    title: 'Por Que',
    titleHighlight: 'MU Vanguard?',
    subtitle: 'Não somos apenas mais um servidor privado. Somos excelência.',
    vanguard: 'MU Vanguard',
    otherServers: 'Outros Servidores',
    features: [{
      icon: Zap,
      title: 'Desenvolvimento Ativo',
      good: 'Atualizações semanais, bugs corrigidos em 24-48h.',
      bad: 'Servidor abandonado após 3 meses. Bugs sem correção por semanas.'
    }, {
      icon: TrendingUp,
      title: 'Economia Balanceada',
      good: 'Taxas ajustadas. Anti-duping ativo. Mercado estável.',
      bad: 'Inflação descontrolada. Dupers arruínam o mercado. Economia morta.'
    }, {
      icon: Crown,
      title: 'Infraestrutura Premium',
      good: 'Servidores dedicados. 99.9% uptime. Backups a cada hora. Proteção DDoS.',
      bad: 'Hospedagem barata compartilhada. Quedas frequentes. Sem backups. Vulnerável.'
    }, {
      icon: Heart,
      title: 'Comunidade Primeiro',
      good: 'Votações comunitárias. Discord ativo. Eventos por jogadores.',
      bad: 'Comunidade tóxica sem moderação. Feedback ignorado. Sem eventos.'
    }, {
      icon: Users,
      title: 'Staff Profissional',
      good: 'Equipe 24/7. Suporte rápido. Decisões transparentes.',
      bad: 'Staff ausente. Tickets ignorados. Decisões arbitrárias.'
    }],
    commitment: 'Nosso Compromisso',
    commitmentText: 'MU Vanguard não é um projeto temporário. É um investimento de longo prazo em criar a melhor experiência de MU Online possível. Cada decisão é tomada pensando na saúde do servidor e na satisfação da comunidade.'
  }
};
export function WhyVanguard() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section className="py-16 md:py-24 relative z-10 bg-darkerBlue overflow-hidden" data-id="element-441">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5" data-id="element-442">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold rounded-full blur-[100px]" data-id="element-443" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-glowBlue rounded-full blur-[100px]" data-id="element-444" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-445">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-446">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" data-id="element-447">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-448">{t.titleHighlight}</span>
          </h2>
          <div className="w-20 h-1 bg-gold mx-auto rounded-full mb-4" data-id="element-449" />
          <p className="text-base text-gray-400 max-w-2xl mx-auto" data-id="element-450">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Compact Comparison Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="glass-panel rounded-xl border border-white/10 overflow-hidden mb-8" data-id="element-451">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_1fr_1fr] md:grid-cols-[220px_1fr_1fr] border-b border-white/10" data-id="element-452">
            <div className="p-4 bg-white/5" data-id="element-453" />
            <div className="p-4 bg-green-500/10 border-l border-white/10 text-center" data-id="element-454">
              <span className="text-sm md:text-base font-bold text-gold font-cinzel flex items-center justify-center gap-2" data-id="element-455">
                <CheckCircle2 className="w-5 h-5" data-id="element-456" />
                {t.vanguard}
              </span>
            </div>
            <div className="p-4 bg-red-500/10 border-l border-white/10 text-center" data-id="element-457">
              <span className="text-sm md:text-base font-bold text-red-400 font-cinzel flex items-center justify-center gap-2" data-id="element-458">
                <XCircle className="w-5 h-5" data-id="element-459" />
                {t.otherServers}
              </span>
            </div>
          </div>

          {/* Rows */}
          {t.features.map((feature, index) => {
          const Icon = feature.icon;
          return <div key={index} className={`grid grid-cols-[1fr_1fr_1fr] md:grid-cols-[220px_1fr_1fr] ${index < t.features.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/[0.02] transition-colors`} data-id="element-460">
                {/* Feature Name */}
                <div className="p-4 flex items-center gap-3" data-id="element-461">
                  <Icon className="w-5 h-5 text-gold flex-shrink-0" data-id="element-462" />
                  <span className="text-sm md:text-base font-semibold text-white font-cinzel leading-tight" data-id="element-463">
                    {feature.title}
                  </span>
                </div>

                {/* Vanguard */}
                <div className="p-4 border-l border-white/5 bg-green-500/[0.03]" data-id="element-464">
                  <p className="text-sm text-gray-300 leading-relaxed flex gap-2" data-id="element-465">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" data-id="element-466" />
                    <span data-id="element-467">{feature.good}</span>
                  </p>
                </div>

                {/* Others */}
                <div className="p-4 border-l border-white/5 bg-red-500/[0.03]" data-id="element-468">
                  <p className="text-sm text-gray-500 leading-relaxed flex gap-2" data-id="element-469">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-470" />
                    <span className="line-through decoration-red-500/40" data-id="element-471">
                      {feature.bad}
                    </span>
                  </p>
                </div>
              </div>;
        })}
        </motion.div>

        {/* Compact Commitment */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="glass-panel rounded-xl p-6 border border-gold/20 bg-gradient-to-r from-gold/5 to-transparent flex items-center gap-5" data-id="element-472">
          <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center border border-gold/30 flex-shrink-0" data-id="element-473">
            <Shield className="w-6 h-6 text-gold" data-id="element-474" />
          </div>
          <div data-id="element-475">
            <h3 className="text-base font-bold text-gold font-cinzel mb-1" data-id="element-476">
              {t.commitment}
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed" data-id="element-477">
              {t.commitmentText}
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}